import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';

const EFFECTIVE_DATE = 'June 7, 2026';
const CONTACT_EMAIL = 'support@dynaparse.com';
const SITE_URL = 'https://dynaparse.com';

function Section({ title, children }) {
  return (
    <div className='mb-8'>
      <h2 className='text-base font-heading font-semibold text-foreground mb-3'>{title}</h2>
      <div className='text-sm text-muted-foreground leading-relaxed space-y-3'>{children}</div>
    </div>
  );
}

export default function RefundPage() {
  return (
    <div className='min-h-screen bg-background'>
      {/* Minimal header */}
      <header className='sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b border-border'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between'>
          <Link to='/' className='flex items-center gap-2'>
            <div className='w-8 h-8 rounded-lg bg-primary flex items-center justify-center'>
              <Zap className='w-4 h-4 text-primary-foreground' strokeWidth={2.2} />
            </div>
            <span className='font-heading font-bold text-lg text-foreground'>
              Dyna<span className='text-primary'>Parse</span>
            </span>
          </Link>
          <Link
            to='/'
            className='text-sm text-muted-foreground hover:text-foreground transition-colors'
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className='max-w-3xl mx-auto px-4 sm:px-6 py-12'>
        <h1 className='text-3xl font-heading font-bold text-foreground mb-1'>Refund Policy</h1>
        <p className='text-sm text-muted-foreground mb-8'>
          Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp;{' '}
          <a href={SITE_URL} className='text-primary hover:underline'>
            {SITE_URL}
          </a>
        </p>

        <Section title='1. Overview'>
          <p>
            DynaParse operates on a pay-per-use credit model. This Refund Policy explains the
            conditions under which refunds are granted. By purchasing credits, you agree to this policy.
          </p>
        </Section>

        <Section title='2. Refund Eligibility'>
          <p>
            Credit pack purchases are eligible for a full refund within{' '}
            <strong className='text-foreground'>14 days</strong> of the transaction date.
            To request a refund, contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className='text-primary hover:underline'>
              {CONTACT_EMAIL}
            </a>
            . Refunds are processed by Paddle, our authorised reseller, in accordance with{' '}
            <a
              href='https://www.paddle.com/legal/refunds'
              className='text-primary hover:underline'
              target='_blank'
              rel='noreferrer'
            >
              Paddle&apos;s refund policy
            </a>
            .
          </p>
        </Section>

        <Section title='3. Non-Refundable Items'>
          <p>The following are not eligible for refunds:</p>
          <ul className='list-disc pl-5 space-y-1'>
            <li>Refund requests made more than 14 days after purchase</li>
            <li>Accounts suspended due to violation of our Terms of Service</li>
            <li>
              Bonus credits (free credits given on sign-up) — these have no monetary value
            </li>
          </ul>
        </Section>

        <Section title='4. How to Request a Refund'>
          <p>
            To request a refund, email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className='text-primary hover:underline'>
              {CONTACT_EMAIL}
            </a>{' '}
            with:
          </p>
          <ul className='list-disc pl-5 space-y-1'>
            <li>Your registered email address</li>
            <li>The transaction / order reference number</li>
            <li>Reason for the refund request</li>
          </ul>
          <p>
            We will respond within 5 business days. Approved refunds are processed within
            10 business days through the original payment method via Paddle.
          </p>
        </Section>

        <Section title='5. Partial Refunds'>
          <p>
            Partial refunds are not available. Refunds apply to the full credit pack purchase.
          </p>
        </Section>

        <Section title='6. Service Errors'>
          <p>
            If an extraction fails due to a bug or server error on our end, the credit will be
            restored to your account automatically. These credits are not subject to monetary refund
            but will be fully credited back to your balance.
          </p>
        </Section>

        <Section title='7. Contact'>
          <p>
            Questions about this policy? Contact us at:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className='text-primary hover:underline font-medium'>
              {CONTACT_EMAIL}
            </a>
          </p>
          <p className='text-foreground/70'>
            DynaParse &nbsp;·&nbsp;{' '}
            <a href={SITE_URL} className='text-primary hover:underline'>
              dynaparse.com
            </a>
          </p>
        </Section>

        <div className='border-t border-border pt-6 mt-4 flex gap-4 text-sm'>
          <Link to='/privacy' className='text-primary hover:underline'>Privacy Policy</Link>
          <Link to='/terms' className='text-primary hover:underline'>Terms of Service</Link>
          <Link to='/' className='text-muted-foreground hover:text-foreground'>← Back to Home</Link>
        </div>
      </main>
    </div>
  );
}
